/**
 * ECDSA P-256 + SHA-256 (Web Crypto) per attestato registrazione — allineato a elitefitness-react-app.
 */

const DB_NAME = 'elitefitness-registration-signing';
const DB_VER = 1;
const STORE = 'keys';
const SLOT = 'ecdsa-p256-reg';

let sessionFallbackPair = null;

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function openDbOnce() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'slot' });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

/** WebKit (IndexedDB su SQLite) a volte fallisce in modo transitorio; retry brevi + fallback in sessione. */
async function openDb() {
  let last;
  for (let i = 0; i < 3; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await openDbOnce();
    } catch (e) {
      last = e;
      if (i < 2) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(100 * (i + 1));
      }
    }
  }
  throw last;
}

async function loadStoredRegistrationSigningKeyPair() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const st = tx.objectStore(STORE);
    const g = st.get(SLOT);
    g.onerror = () => reject(g.error);
    g.onsuccess = () => {
      const row = g.result;
      if (!row?.privateKey || !row?.publicKey) {
        resolve(null);
        return;
      }
      resolve({ privateKey: row.privateKey, publicKey: row.publicKey });
    };
  });
}

async function persistRegistrationSigningKeyPair(pair) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const st = tx.objectStore(STORE);
    const put = st.put({ slot: SLOT, privateKey: pair.privateKey, publicKey: pair.publicKey });
    put.onerror = () => reject(put.error);
    put.onsuccess = () => resolve();
  });
}

export function assertSubtle() {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error('Firma digitale non disponibile in questo ambiente. Usa HTTPS o aggiorna il browser.');
  }
  return crypto.subtle;
}

export async function getOrCreateRegistrationSigningKeyPair() {
  assertSubtle();
  let existing = null;
  try {
    existing = await loadStoredRegistrationSigningKeyPair();
  } catch (e) {
    console.warn('[registration-signing] IndexedDB non disponibile, uso chiavi solo in questa sessione', e);
  }
  if (existing) {
    return existing;
  }
  if (sessionFallbackPair) {
    return sessionFallbackPair;
  }
  const pair = await crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['sign', 'verify'],
  );
  try {
    await persistRegistrationSigningKeyPair(pair);
  } catch {
    sessionFallbackPair = pair;
  }
  return pair;
}

export async function exportPublicJwk(publicKey) {
  assertSubtle();
  return crypto.subtle.exportKey('jwk', publicKey);
}

export async function signCanonicalPayloadUtf8(privateKey, canonicalPayload) {
  assertSubtle();
  const enc = new TextEncoder();
  const data = enc.encode(canonicalPayload);
  return crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, privateKey, data);
}

function bufferToHexLower(buf) {
  const a = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < a.length; i += 1) {
    s += a[i].toString(16).padStart(2, '0');
  }
  return s;
}

export async function assertSha256Utf8Matches(canonicalPayload, expectedHexLower) {
  assertSubtle();
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest('SHA-256', enc.encode(canonicalPayload));
  const hex = bufferToHexLower(buf);
  if (hex !== String(expectedHexLower).toLowerCase()) {
    throw new Error('Mismatch tra payload e impronta SHA-256 dal server.');
  }
}

export function signatureDerToStandardBase64(derSignature) {
  const bytes = new Uint8Array(derSignature);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) {
    bin += String.fromCharCode(bytes[i]);
  }
  return btoa(bin);
}
